import {useCallback, useMemo, useState} from "react";
import type {
    DataGridPremiumProps,
    GridFilterModel,
    GridPaginationModel,
    GridSortModel,
} from "@mui/x-data-grid-premium";
import {DATA_GRID_DEFAULT_PAGE_SIZE, EMPTY_FILTER_MODEL} from "./data-grid.config";

const EMPTY_SORT_MODEL: GridSortModel = [];

/** A filter item only means something to the API once it actually carries a value. */
function hasFilterValue(value: unknown): boolean {
    if (value === undefined || value === null || value === '') return false;
    return !(Array.isArray(value) && value.length === 0);
}

/**
 * DRF `?ordering=` — `-` prefix for descending, comma-separated for multi-sort. A column whose
 * API ordering key differs from its grid `field` (a nested lookup like `country__code_a2`) is
 * renamed through `orderingFields`; any other column is sent under its own field name.
 */
function orderingFromSortModel(
    model: GridSortModel,
    orderingFields: Partial<Record<string, string>> | undefined,
): string | undefined {
    const fields = model
        .filter(item => item.sort)
        .map(item => {
            const key = orderingFields?.[item.field] ?? item.field;
            return item.sort === 'desc' ? `-${key}` : key;
        });

    return fields.length > 0 ? fields.join(',') : undefined;
}

/** The server-side wiring to spread onto `CustomDataGrid`; `rowCount` and `loading` stay with the caller. */
export type DataGridServerProps = Required<Pick<DataGridPremiumProps,
    | 'paginationMode'
    | 'filterMode'
    | 'sortingMode'
    | 'paginationModel'
    | 'onPaginationModelChange'
    | 'filterModel'
    | 'onFilterModelChange'
    | 'sortModel'
    | 'onSortModelChange'>>;

export type DataGridQueryStateOptions = {
    /** Rows per page to start on — defaults to `DATA_GRID_DEFAULT_PAGE_SIZE`. */
    pageSize?: number;
    initialFilterModel?: GridFilterModel;
    initialSortModel?: GridSortModel;
    /**
     * A page-level filter that lives outside the grid (a search box, a view toggle). When its value
     * changes the current page number no longer means anything, so paging jumps back to the first page.
     */
    resetPageOn?: unknown;
    /**
     * Grid column `field` → DRF ordering key, for columns whose sort key isn't the field itself
     * (e.g. `{country: 'country__code_a2'}`). Read on every render, so keep it a module constant.
     */
    orderingFields?: Partial<Record<string, string>>;
};

export type DataGridQueryState = {
    paginationModel: GridPaginationModel;
    filterModel: GridFilterModel;
    sortModel: GridSortModel;
    /** 1-based page number for DRF's `?page=` (the grid's own model is 0-based). */
    page: number;
    pageSize: number;
    /** `?ordering=` value, or undefined while nothing is sorted. */
    ordering: string | undefined;
    /** True when at least one filter item carries a value — tells "empty result" from "empty table". */
    hasActiveFilters: boolean;
    /** Back to the first page, for resets the hook can't see. */
    resetPage: () => void;
    setPaginationModel: (model: GridPaginationModel) => void;
    setFilterModel: (model: GridFilterModel) => void;
    setSortModel: (model: GridSortModel) => void;
    dataGridProps: DataGridServerProps;
};

/**
 * Pagination, filter and sort state for one server-driven data grid.
 *
 * Spread `dataGridProps` onto `CustomDataGrid` and feed `page` / `pageSize` / `ordering` plus the
 * resource's own filter params to the list query. Changing a filter or the sort order resets the page,
 * because page 4 of the old result set says nothing about the new one.
 */
export function useDataGridQueryState({
                                          pageSize = DATA_GRID_DEFAULT_PAGE_SIZE,
                                          initialFilterModel = EMPTY_FILTER_MODEL,
                                          initialSortModel = EMPTY_SORT_MODEL,
                                          resetPageOn,
                                          orderingFields,
                                      }: DataGridQueryStateOptions = {}): DataGridQueryState {
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({page: 0, pageSize});
    const [filterModel, setFilterModelState] = useState<GridFilterModel>(initialFilterModel);
    const [sortModel, setSortModelState] = useState<GridSortModel>(initialSortModel);

    const firstPage = useCallback(() => {
        setPaginationModel(prev => prev.page === 0 ? prev : {...prev, page: 0});
    }, []);

    // Render-phase reset rather than an effect — see "Effects and state" in AGENTS.md.
    const [prevResetPageOn, setPrevResetPageOn] = useState(resetPageOn);
    if (prevResetPageOn !== resetPageOn) {
        setPrevResetPageOn(resetPageOn);
        firstPage();
    }

    const setFilterModel = useCallback((model: GridFilterModel) => {
        setFilterModelState(model);
        firstPage();
    }, [firstPage]);

    const setSortModel = useCallback((model: GridSortModel) => {
        setSortModelState(model);
        firstPage();
    }, [firstPage]);

    const ordering = useMemo(() => orderingFromSortModel(sortModel, orderingFields), [sortModel, orderingFields]);
    const hasActiveFilters = useMemo(
        () => filterModel.items.some(item => hasFilterValue(item.value)),
        [filterModel],
    );

    return {
        paginationModel,
        filterModel,
        sortModel,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        ordering,
        hasActiveFilters,
        resetPage: firstPage,
        setPaginationModel,
        setFilterModel,
        setSortModel,
        dataGridProps: {
            paginationMode: 'server',
            filterMode: 'server',
            sortingMode: 'server',
            paginationModel,
            onPaginationModelChange: setPaginationModel,
            filterModel,
            onFilterModelChange: setFilterModel,
            sortModel,
            onSortModelChange: setSortModel,
        },
    };
}
